import React from 'react';

/**
 * Calls the provided callback when the asset menu completes a CSS transition that affects
 * layout (e.g., width), or when the window is resized/scrolled.
 *
 * It locates the asset menu via the data attribute set on the root menu element.
 */
export const useAssetMenuTransition = (onTransitionComplete: () => void) => {
  React.useEffect(() => {
    const menu = document.querySelector('[data-asset-menu-root="true"]');
    if (!(menu instanceof HTMLElement)) {
      return;
    }

    const handleTransitionEnd = (e: Event) => {
      // Only react to transitions that affect size/position (like width or left)
      if (e instanceof TransitionEvent) {
        const property = e.propertyName;
        if (
          property === 'width' ||
          property === 'left' ||
          property === 'right' ||
          property === 'margin-left' ||
          property === 'transform'
        ) {
          onTransitionComplete();
        }
      } else {
        onTransitionComplete();
      }
    };

    const handleResizeOrScroll = () => onTransitionComplete();

    menu.addEventListener('transitionend', handleTransitionEnd);
    window.addEventListener('resize', handleResizeOrScroll, { passive: true });
    window.addEventListener('scroll', handleResizeOrScroll, { passive: true });

    // Fallback: observe size changes without relying on specific CSS property names
    let ro: ResizeObserver | undefined;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(() => onTransitionComplete());
      ro.observe(menu);
    }

    return () => {
      menu.removeEventListener('transitionend', handleTransitionEnd);
      window.removeEventListener('resize', handleResizeOrScroll);
      window.removeEventListener('scroll', handleResizeOrScroll);
      if (ro) {
        ro.disconnect();
      }
    };
  }, [onTransitionComplete]);
};
